import React from "react";
import { Button } from "primereact/button";
import { AppCard } from "../../components/ui";
import { Dropdown } from "primereact/dropdown";
import { FileSelectionScreenProps } from "./types";
import { isSuperAdmin } from "../../authGuard";
import ErrorDisplay from "./ErrorDisplay";

const FileSelectionScreen: React.FC<FileSelectionScreenProps> = ({
  selectedFile,
  validationResult,
  isUploading,
  onFileSelect,
  onUpload,
  selectedChannel,
  onChannelChange,
  selectedMerchant,
  onMerchantChange,
  merchants = []
}) => {
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    onFileSelect(file || null);
  };

  const handleChannelChange = (e: any) => {
    if (onChannelChange) {
      onChannelChange(e.value);
    }
  };

  const handleMerchantChange = (e: any) => {
    if (onMerchantChange) {
      onMerchantChange(e.value);
    }
  };

  const isSuperAdminUser = isSuperAdmin();

  return (
    <AppCard>
      <AppCard.Header title="Upload Payment File (.081 or .txt)" />
      <AppCard.Body>
        {/* Role-based dropdown selection */}
        <div className="mb-4">
          {isSuperAdminUser ? (
            // Two dropdowns in one line
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="font-semibold text-base block mb-3 text-neutral-600 dark:text-neutral-400 tracking-wide">Select Merchant</label>
                <Dropdown
                  value={selectedMerchant}
                  onChange={handleMerchantChange}
                  options={merchants}
                  optionLabel="label"
                  optionValue="value"
                  filter
                  showClear
                  filterBy="label"
                  filterPlaceholder="Search merchants..."
                  placeholder="Select Merchant"
                  className="w-full rounded-lg border-2 border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-sm min-h-[48px]"
                />
              </div>
              <div className="mb-4">
                <label className="font-semibold text-base block mb-3 text-neutral-600 dark:text-neutral-400 tracking-wide">Select Channel</label>
                <Dropdown
                  value={selectedChannel}
                  onChange={handleChannelChange}
                  options={[
                    { label: 'BULK (BISERA)', value: 'BISERA' },
                    { label: 'Instant (INST)', value: 'INST' }
                  ]}
                  placeholder="Select Channel"
                  className="w-full rounded-lg border-2 border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-sm min-h-[48px]"
                />
              </div>
            </div>
          ) : (
            // Single dropdown full width
            <div className="mb-4">
              <label className="font-semibold text-base block mb-3 text-neutral-600 dark:text-neutral-400 tracking-wide">Select Channel</label>
              <Dropdown
                value={selectedChannel}
                onChange={handleChannelChange}
                options={[
                  { label: 'BULK (BISERA)', value: 'BISERA' },
                  { label: 'Instant (INST)', value: 'INST' }
                ]}
                placeholder="Select Channel"
                className="w-full rounded-lg border-2 border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-sm min-h-[48px]"
              />
            </div>
          )}
        </div>

        <div className="text-center mb-4">
          <div
            className={`border-2 border-dashed border-primary-500 rounded-[10px] py-10 px-5 bg-neutral-50 dark:bg-neutral-900 cursor-pointer transition-all relative hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:border-primary-600 ${isUploading ? 'bg-primary-50 dark:bg-primary-900/20 cursor-not-allowed opacity-70' : ''}`}
            onClick={() => !isUploading && document.getElementById('fileInput')?.click()}
          >
            <i className="fas fa-cloud-upload-alt text-5xl text-primary-500 mb-4"></i>
            <div className="text-lg font-bold text-neutral-700 dark:text-neutral-300 mb-2">
              {selectedFile ? selectedFile.name : 'Choose .081 or .txt File'}
            </div>
            <div className="text-sm text-neutral-500 dark:text-neutral-400">
              Click here or drag and drop your .081 or .txt file
            </div>
            <input
              type="file"
              id="fileInput"
              onChange={handleFileSelect}
              accept=".081,.txt"
              disabled={isUploading}
              className="hidden"
            />
          </div>
        </div>

        <div className="text-center">
          <Button
            onClick={onUpload}
            disabled={!selectedFile || isUploading}
            className="rounded px-8 py-3 min-w-[160px] text-sm font-medium bg-primary-500 text-white border border-primary-500 h-11 inline-flex items-center justify-center hover:bg-primary-600 disabled:bg-neutral-500 disabled:cursor-not-allowed disabled:opacity-60"
            icon="fas fa-upload"
            label={isUploading ? 'Uploading...' : 'Upload File'}
          />
        </div>

        {validationResult && (
          <ErrorDisplay error={validationResult} />
        )}
      </AppCard.Body>
    </AppCard>
  );
};

export default FileSelectionScreen;
